import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { projectsApi } from '../../api/projects';

/**
 * Series-room guard. Redirects to project Discovery when the project
 * is not a series.
 *
 * First click on Timeline from the left map already has the series in
 * AppContext. Waiting on a one-shot GET painted a blank white page
 * until that fetch finished — or forever if the effect was cancelled
 * on the client-navigation remount. Use the in-memory project when it
 * matches the route. Fetch only when context does not already know.
 */
export default function RequireSeriesProject({ children }) {
  const { projectId: routeProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const queryProjectId = searchParams.get('project');
  const resolvedId = routeProjectId || queryProjectId;
  const { currentProject } = useApp();

  const fromContext = currentProject?.id === resolvedId ? currentProject : undefined;
  const [fetched, setFetched] = useState(undefined);
  const project = fromContext !== undefined ? fromContext : fetched;

  useEffect(() => {
    if (!resolvedId) {
      setFetched(null);
      return;
    }
    if (fromContext !== undefined) return;
    let cancelled = false;
    projectsApi.get(resolvedId)
      .then((p) => { if (!cancelled) setFetched(p); })
      .catch(() => { if (!cancelled) setFetched(null); });
    return () => { cancelled = true; };
  }, [resolvedId, fromContext]);

  if (project === undefined) return null;

  if (!project || !project.is_series) {
    if (resolvedId) {
      return <Navigate to={`/projects/${resolvedId}/discovery`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}
